import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingMetricInput,
    DeleteLandingMetricInput,
    LandingMetric,
    ModelLandingMetricFilterInput,
    UpdateLandingMetricInput
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class LandingMetricService {
    private amplifyClient = generateClient();
    private _cachedMetrics: Map<string, LandingMetric[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<string, Promise<LandingMetric[]>> = new Map();

    private _getCacheKey(filter: ModelLandingMetricFilterInput): string {
        return JSON.stringify(filter || {});
    }

    async listLandingMetrics(
        filter: ModelLandingMetricFilterInput = null
    ): Promise<LandingMetric[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedMetrics.has(cacheKey)) {
            return this._cachedMetrics.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchMetrics(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchMetrics(
        filter: ModelLandingMetricFilterInput,
        cacheKey: string
    ): Promise<LandingMetric[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingMetrics,
                    variables: { filter }
                })
            ).data.listLandingMetrics.items;

            if (data) {
                const metrics = data as LandingMetric[];
                this._cachedMetrics.set(cacheKey, metrics);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return metrics;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            console.error('[LandingMetric] listLandingMetrics failed:', error);
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedMetrics.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingMetric(id: string): Promise<LandingMetric> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingMetric,
                    variables: { id }
                })
            ).data.getLandingMetric;
            if (data) {
                return data as LandingMetric;
            }
        } catch (error) {
            console.error('Error finding LandingMetric:', error);
        }
        return null;
    }

    async createLandingMetric(
        input: CreateLandingMetricInput
    ): Promise<LandingMetric> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingMetric,
                    variables: { input }
                })
            ).data.createLandingMetric;
            if (data) {
                this.clearCache();
                return data as LandingMetric;
            }
        } catch (error) {
            console.error('Error creating LandingMetric:', error);
        }
        return null;
    }

    async updateLandingMetric(
        input: UpdateLandingMetricInput
    ): Promise<LandingMetric> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingMetric,
                    variables: { input }
                })
            ).data.updateLandingMetric;
            if (data) {
                this.clearCache();
                return data as LandingMetric;
            }
        } catch (error) {
            console.error('Error updating LandingMetric:', error);
        }
        return null;
    }

    async deleteLandingMetric(
        input: DeleteLandingMetricInput
    ): Promise<LandingMetric> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingMetric,
                    variables: { input }
                })
            ).data.deleteLandingMetric;
            if (data) {
                this.clearCache();
                return data as LandingMetric;
            }
        } catch (error) {
            console.error('Error deleting LandingMetric:', error);
        }
        return null;
    }
}
